/**
 * The one place every action in this app records itself.
 *
 * Before this, thirteen trigger names were written from scattered corners of the code
 * and most actions wrote nothing at all. Two consequences, both visible on staging:
 * 212 of 220 deals show "No workflow events recorded for this deal", and nothing on
 * screen could tell you whether a thing had already been done. One seller was sent the
 * same Vehicle Purchase Agreement nine times, and one car had its PPSR registration lodged twice.
 *
 * Three rules hold this together:
 *   - every action writes an event, whether it succeeded or failed;
 *   - every event carries the person who caused it, unless nothing did;
 *   - a deliberate repeat says so, and says why.
 */
import { supabase } from '../lib/supabase'
import { workflowLogFields } from '../lib/workflowLogStatus'

/**
 * Every action that leaves a trace. The value is what lands in
 * workflow_logs.trigger_name, so the names already in the table are kept exactly as
 * they are - renaming one would orphan the history sitting behind it.
 */
export const ACTIONS = {
  DEAL_CREATED: 'DEAL_CREATED',
  DEAL_EDITED: 'DEAL_EDITED',
  DEAL_ARCHIVED: 'DEAL_ARCHIVED',
  STAGE_CHANGED: 'STAGE_CHANGED',
  BOS_GENERATED: 'BOS_GENERATED',
  BOS_EMAIL_SENT: 'BOS_EMAIL_SENT',
  BOS_SMS_SENT: 'BOS_SMS_SENT',
  SIGNATURE_CAPTURED: 'SIGNATURE_CAPTURED',
  DD_COLLECTION_INITIATED: 'DD_COLLECTION_INITIATED',
  INSPECTION_BOOKING_INITIATED: 'INSPECTION_BOOKING_INITIATED',
  INSPECTION_OUTCOME_RECORDED: 'INSPECTION_OUTCOME_RECORDED',
  BUYER_DETAILS_SAVED: 'BUYER_DETAILS_SAVED',
  /* Writing the signed contract to the document store. Distinct from the signature
     itself, which is the seller's act - this one is ours. */
  BOS_DOCUMENT_FILED: 'BOS_DOCUMENT_FILED',
  IMAGE_GENERATED: 'IMAGE_GENERATED',
  IMAGE_REMOVED: 'IMAGE_REMOVED',
  INVOICE_GENERATED: 'INVOICE_GENERATED',
  INVOICE_EMAILED: 'INVOICE_EMAILED',
  PAYMENT_RECORDED: 'PAYMENT_RECORDED',
  PAYMENT_RUN_INITIATED: 'PAYMENT_RUN_INITIATED',
  AR_REMINDER_SENT: 'AR_REMINDER_SENT',
  PPSR_SEARCHED: 'PPSR_REVALIDATED',
  PPSR_REGISTRATION_LODGED: 'PPSR_REGISTRATION_LODGED',
  PPSR_REGISTRATION_SUBMITTED: 'PPSR_REGISTRATION_SUBMITTED',
  PPSR_DISCHARGE_INITIATED: 'PPSR_DISCHARGE_INITIATED',
  PPSR_DISCHARGED: 'PPSR_DISCHARGED',
  XERO_SYNC_INITIATED: 'XERO_SYNC_INITIATED',
  SOURCING_DISMISSED: 'SOURCING_DISMISSED',
  SOURCING_RESTORED: 'SOURCING_RESTORED',
  /* Winnie, 18/20 Sep. Each of these is a deliberate act against a finalised deal, which is
     exactly the class of thing the action history exists to make findable afterwards. */
  SALE_AMENDED: 'SALE_AMENDED',
  SALE_VOIDED: 'SALE_VOIDED',
  FIELD_CORRECTED: 'FIELD_CORRECTED',
  VEHICLE_RETURNED_TO_SELLER: 'VEHICLE_RETURNED_TO_SELLER',
} as const

export type ActionName = (typeof ACTIONS)[keyof typeof ACTIONS]

/**
 * What each action is called on screen. Past tense, because every one of these is the
 * record of a thing that already happened - "Vehicle Purchase Agreement emailed", not "Email".
 */
export const ACTION_LABELS: Record<string, string> = {
  DEAL_CREATED: 'Deal created',
  DEAL_EDITED: 'Deal edited',
  DEAL_ARCHIVED: 'Deal archived',
  STAGE_CHANGED: 'Stage changed',
  BOS_GENERATED: 'Vehicle Purchase Agreement generated',
  BOS_EMAIL_SENT: 'Vehicle Purchase Agreement emailed',
  BOS_SMS_SENT: 'Vehicle Purchase Agreement texted',
  SIGNATURE_CAPTURED: 'Signature captured',
  DD_COLLECTION_INITIATED: 'Due-diligence documents requested',
  INSPECTION_BOOKING_INITIATED: 'Inspection booked',
  INSPECTION_OUTCOME_RECORDED: 'Inspection outcome recorded',
  BUYER_DETAILS_SAVED: 'Buyer details saved',
  BOS_DOCUMENT_FILED: 'Signed contract filed',
  IMAGE_GENERATED: 'Image generated',
  IMAGE_REMOVED: 'Image removed',
  INVOICE_GENERATED: 'Invoice generated',
  INVOICE_EMAILED: 'Invoice emailed',
  PAYMENT_RECORDED: 'Payment recorded',
  PAYMENT_RUN_INITIATED: 'Payment run started',
  AR_REMINDER_SENT: 'AR reminder sent',
  PPSR_REVALIDATED: 'PPSR searched',
  PPSR_REGISTRATION_LODGED: 'PPSR registration lodged',
  PPSR_REGISTRATION_SUBMITTED: 'PPSR registration submitted',
  PPSR_DISCHARGE_INITIATED: 'PPSR discharge started',
  PPSR_DISCHARGED: 'PPSR discharged',
  XERO_SYNC_INITIATED: 'Xero sync started',
  SOURCING_DISMISSED: 'Taken off the sourcing list',
  SOURCING_RESTORED: 'Put back on the sourcing list',
  SALE_AMENDED: 'Sale amended',
  SALE_VOIDED: 'Sale voided',
  FIELD_CORRECTED: 'Field corrected',
  VEHICLE_RETURNED_TO_SELLER: 'Vehicle returned to seller',
}

export const actionLabel = (name: string): string => ACTION_LABELS[name] || name

export interface ActionEvent {
  id: string
  dealId: string
  action: string
  success: boolean
  details: Record<string, unknown> | null
  errorMessage: string | null
  executedAt: string
  actorId: string | null
  actorName: string | null
  repeatOf: string | null
  repeatReason: string | null
}

export interface LogInput {
  dealId: string
  action: string
  /** The signed-in person. Leave unset only when nothing human caused this. */
  actorId?: string | null
  success?: boolean
  details?: Record<string, unknown> | null
  errorMessage?: string | null
  /** Set both of these when the action is being done a second time, on purpose. */
  repeatOf?: string | null
  repeatReason?: string | null
}

/**
 * Write one event. Returns its id, or null if the write failed.
 *
 * This never throws. An action that actually worked must not be reported to the user
 * as a failure because the audit row would not save - but the failure is shouted at
 * the console, because a silently missing audit trail is exactly what got us here.
 */
export async function logAction(input: LogInput): Promise<string | null> {
  try {
    const row = {
      deal_id: input.dealId,
      trigger_name: input.action,
      ...workflowLogFields(
        input.success === false ? 'failed' : 'success',
        input.errorMessage ?? null,
      ),
      details: input.details || null,
      actor_user_id: input.actorId || null,
      repeat_of: input.repeatOf || null,
      repeat_reason: input.repeatReason || null,
      executed_at: new Date().toISOString(),
    }

    const { data, error } = await supabase
      .from('workflow_logs')
      .insert(row)
      .select('id')
      .single()

    if (error) throw error
    return (data && data.id) || null
  } catch (err) {
    console.error('Could not record the action ' + input.action, err)
    return null
  }
}

interface RawRow {
  id: string
  deal_id: string
  trigger_name: string
  success: boolean | null
  details: Record<string, unknown> | null
  error_message: string | null
  executed_at: string | null
  created_at: string | null
  actor_user_id: string | null
  repeat_of: string | null
  repeat_reason: string | null
  actor?: { full_name?: string | null; email?: string | null } | null
}

const toEvent = (r: RawRow): ActionEvent => ({
  id: r.id,
  dealId: r.deal_id,
  action: r.trigger_name,
  success: r.success !== false,
  details: r.details,
  errorMessage: r.error_message,
  // executed_at is the truth, but the oldest rows predate it being set.
  executedAt: r.executed_at || r.created_at || '',
  actorId: r.actor_user_id,
  actorName: (r.actor && (r.actor.full_name || r.actor.email)) || null,
  repeatOf: r.repeat_of,
  repeatReason: r.repeat_reason,
})

const SELECT =
  'id, deal_id, trigger_name, success, details, error_message, executed_at, created_at, actor_user_id, repeat_of, repeat_reason, actor:actor_user_id (full_name, email)'

/** Everything that has happened to one deal, newest first. */
export async function loadActionHistory(dealId: string): Promise<ActionEvent[]> {
  try {
    const { data, error } = await supabase
      .from('workflow_logs')
      .select(SELECT)
      .eq('deal_id', dealId)
      .order('executed_at', { ascending: false, nullsFirst: false })

    if (error) throw error
    return ((data || []) as unknown as RawRow[]).map(toEvent)
  } catch (err) {
    console.error('Could not read the history for this deal', err)
    return []
  }
}

/**
 * The most recent event for each action. Feed it the list above, which already arrives
 * newest first, so the first one seen for a name is the latest.
 */
export function lastByAction(events: ActionEvent[]): Record<string, ActionEvent> {
  const out: Record<string, ActionEvent> = {}
  for (const e of events) {
    if (!out[e.action]) out[e.action] = e
  }
  return out
}

/** How many times an action has been performed on this deal. */
export function countByAction(events: ActionEvent[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const e of events) {
    out[e.action] = (out[e.action] || 0) + 1
  }
  return out
}

/** "22 Aug, 3:14pm" - short enough to sit on a card. */
export function shortWhen(iso: string): string {
  if (!iso) return 'at an unrecorded time'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'at an unrecorded time'
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * "22 Aug, 3:14pm by Winnie Tran". An event with no actor is not an anonymous person,
 * it is the scheduler, and saying so is more useful than leaving a blank.
 */
export function whenAndWho(e: ActionEvent): string {
  const when = shortWhen(e.executedAt)
  return e.actorName ? when + ' by ' + e.actorName : when + ', automatically'
}
